import React, { useMemo } from 'react';
import {
    Box,
    Center,
    Flex,
    Grid,
    Heading,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    HStack,
    Progress,
    Badge,
    useColorMode,
} from '@chakra-ui/react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts';
import { useNogleTech } from '../../../contexts/NogleTechContext';
import { Panel, KpiCard } from '../shared/Card';
import { PrioridadeBadge } from '../shared/badges';
import EmManutencao from '../../../components/UI/EmManutencao';

const COLORS = {
    neon: '#02EA75',
    green: '#02BD6D',
    blue: '#3182CE',
    red: '#E53E3E',
    yellow: '#ECC94B',
    purple: '#B794F4',
    beige: '#F2EDDA',
    gray: '#4A5568',
};

const ENTREGAS = [
    { key: 'site_feito_contel', label: 'Site' },
    { key: 'app_google_feito_contel', label: 'App Android' },
    { key: 'app_apple_feito_contel', label: 'App iOS' },
    { key: 'painel_feito_contel', label: 'Painel' },
    { key: 'venda_direta_feito_contel', label: 'Venda Direta' },
];

const Dashboard = () => {
    const { marcas, vendas, loading, indicadores, toBool } = useNogleTech();
    const { colorMode } = useColorMode();

    const provedoresData = useMemo(() =>
        Object.entries(indicadores.provedores || {})
            .map(([name, total]) => ({ name, total }))
            .sort((a, b) => b.total - a.total)
    , [indicadores.provedores]);

    const appData = useMemo(() => {
        const com = indicadores.comApp;
        const sem = indicadores.total - com;
        return [
            { name: 'Com App', value: com, color: COLORS.neon },
            { name: 'Sem App', value: sem, color: COLORS.gray },
        ].filter((d) => d.value > 0);
    }, [indicadores]);

    const entregasData = useMemo(() =>
        ENTREGAS.map((e) => ({
            produto: e.label,
            Entregues: marcas.filter((m) => toBool(m[e.key])).length,
            Pendentes: marcas.length - marcas.filter((m) => toBool(m[e.key])).length,
        }))
    , [marcas, toBool]);

    const canaisData = useMemo(() => {
        const campos = [
            { key: 'site', label: 'Site' },
            { key: 'whatsapp', label: 'WhatsApp' },
            { key: 'facebook', label: 'Facebook' },
            { key: 'instagram', label: 'Instagram' },
            { key: 'sac', label: 'SAC' },
        ];
        return campos.map((c) => ({
            name: c.label,
            total: marcas.filter((m) => !!m[c.key]).length,
        }));
    }, [marcas]);

    const taxaApp = indicadores.total > 0
        ? Math.round((indicadores.comApp / indicadores.total) * 100)
        : 0;

    // ⚠️ Tela em produção (pedido do Lucas em 16/07/2026): o Dashboard admin ainda
    // não é usado da forma correta — exibe o aviso padrão "Em breve". Para
    // reativar a tela original, basta voltar esta flag para false (o código
    // completo permanece logo abaixo).
    const DASHBOARD_EM_BREVE = true;
    if (DASHBOARD_EM_BREVE) {
        return (
            <EmManutencao
                titulo="Dashboard"
                descricao="A visão geral de marcas (KPIs, entregas e canais) está em produção e será liberada em breve."
            />
        );
    }

    if (loading) {
        return (
            <Center h="60vh">
                <Spinner size="xl" color="secondary" thickness="4px" />
            </Center>
        );
    }

    return (
        <VStack align="stretch" spacing={6}>
            <Flex justify="space-between" align="center">
                <Box>
                    <Heading size="lg" color="textPrimary" textTransform="uppercase" letterSpacing="2px">
                        Nogle <Text as="span" color={colorMode === 'dark' ? 'primary' : 'white'} bg="secondary" px={3} py={1} borderRadius="md">Tech</Text>
                    </Heading>
                </Box>
                <Text fontSize="xs" color="textSecondary">
                    {new Date().toLocaleString('pt-BR')}
                </Text>
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
                <KpiCard label="Total de Marcas" value={indicadores.total} accent={COLORS.neon} />
                <KpiCard label="Com Aplicativo" value={indicadores.comApp} accent={COLORS.green} />
                <KpiCard label="Com ERP/Provedor" value={indicadores.comProvedor} accent={COLORS.beige} />
                <KpiCard label="Entregas Contel" value={indicadores.entreguesContel} accent={COLORS.blue} />
                <Box
                    bg="surface"
                    borderRadius="xl"
                    p={5}
                    border="1px solid"
                    borderColor="borderPrimary"
                    position="relative"
                    _before={{
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        w: '4px',
                        bg: COLORS.purple,
                    }}
                >
                    <Box fontSize="2.2rem" fontWeight="900" color="textPrimary" lineHeight="1">
                        {taxaApp}%
                    </Box>
                    <Box fontSize="0.65rem" fontWeight="700" textTransform="uppercase" color="textSecondary" mt={2} letterSpacing="1px">
                        Cobertura de App
                    </Box>
                    <Progress
                        value={taxaApp}
                        size="xs"
                        mt={2}
                        colorScheme="green"
                        borderRadius="full"
                        bg={colorMode === 'dark' ? "gray.700" : "gray.200"}
                    />
                </Box>
            </SimpleGrid>

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
                <Panel title="Cobertura de Aplicativo">
                    <Box h="260px">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={appData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                >
                                    {appData.map((e, i) => (
                                        <Cell key={i} fill={e.color} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ background: colorMode === 'dark' ? '#1A1A1A' : '#FFFFFF', border: `1px solid ${colorMode === 'dark' ? '#2D3748' : '#D1D5DB'}`, color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} itemStyle={{ color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} labelStyle={{ color: colorMode === 'dark' ? '#A0A3A8' : '#4A5568' }} />
                                <Legend wrapperStyle={{ fontSize: 12, color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                </Panel>

                <Panel title="Canais Digitais Cadastrados">
                    <Box h="260px">
                        <ResponsiveContainer>
                            <BarChart data={canaisData} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid stroke={colorMode === 'dark' ? '#2D3748' : '#E5E7EB'} strokeDasharray="3 3" />
                                <XAxis type="number" stroke={colorMode === 'dark' ? '#A0AEC0' : '#4B5563'} fontSize={11} />
                                <YAxis dataKey="name" type="category" stroke={colorMode === 'dark' ? '#A0AEC0' : '#4B5563'} fontSize={11} width={100} />
                                <Tooltip contentStyle={{ background: colorMode === 'dark' ? '#1A1A1A' : '#FFFFFF', border: `1px solid ${colorMode === 'dark' ? '#2D3748' : '#D1D5DB'}`, color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} itemStyle={{ color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} labelStyle={{ color: colorMode === 'dark' ? '#A0A3A8' : '#4A5568' }} />
                                <Bar dataKey="total" fill={COLORS.neon} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Panel>
            </Grid>

            <Panel title="Entregas Contel por Produto">
                <Box h="280px">
                    <ResponsiveContainer>
                        <BarChart data={entregasData}>
                            <CartesianGrid stroke={colorMode === 'dark' ? '#2D3748' : '#E5E7EB'} strokeDasharray="3 3" />
                            <XAxis dataKey="produto" stroke={colorMode === 'dark' ? '#A0AEC0' : '#4B5563'} fontSize={10} />
                            <YAxis stroke={colorMode === 'dark' ? '#A0AEC0' : '#4B5563'} fontSize={11} />
                            <Tooltip contentStyle={{ background: colorMode === 'dark' ? '#1A1A1A' : '#FFFFFF', border: `1px solid ${colorMode === 'dark' ? '#2D3748' : '#D1D5DB'}`, color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} itemStyle={{ color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} labelStyle={{ color: colorMode === 'dark' ? '#A0A3A8' : '#4A5568' }} />
                            <Legend wrapperStyle={{ fontSize: 11, color: colorMode === 'dark' ? '#FFFFFF' : '#1F2937' }} />
                            <Bar dataKey="Entregues" stackId="a" fill={COLORS.green} />
                            <Bar dataKey="Pendentes" stackId="a" fill={colorMode === 'dark' ? COLORS.gray : '#D1D5DB'} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Panel>

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
                <Panel title={`Provedores / ERPs (${provedoresData.length})`}>
                    <VStack align="stretch" spacing={2} maxH="260px" overflowY="auto">
                        {provedoresData.length === 0 && (
                            <Text color="textSecondary" textAlign="center" py={10}>
                                Nenhum provedor cadastrado
                            </Text>
                        )}
                        {provedoresData.map((p) => (
                            <HStack
                                key={p.name}
                                bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                px={3}
                                py={2}
                                borderRadius="md"
                                justify="space-between"
                                border="1px solid"
                                borderColor="borderPrimary"
                            >
                                <Text fontSize="sm" fontWeight="700" color="textPrimary">
                                    {p.name}
                                </Text>
                                <Badge bg="rgba(2,234,117,0.15)" color="secondary" fontSize="0.65rem">
                                    {p.total} {p.total === 1 ? 'marca' : 'marcas'}
                                </Badge>
                            </HStack>
                        ))}
                    </VStack>
                </Panel>

                <Panel title={`Vendas Recentes (${vendas.length})`}>
                    <Box maxH="260px" overflowY="auto">
                        <Table size="sm" variant="unstyled">
                            <Thead position="sticky" top={0} bg="surface" zIndex={1}>
                                <Tr>
                                    <Th color="secondary" fontSize="0.65rem">Data</Th>
                                    <Th color="secondary" fontSize="0.65rem">Marca</Th>
                                    <Th color="secondary" fontSize="0.65rem">Solução</Th>
                                    <Th color="secondary" fontSize="0.65rem">Prio</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {vendas.map((v) => (
                                    <Tr key={v.id} _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.50' }}>
                                        <Td fontSize="xs" color="textSecondary">
                                            {new Date(v.data).toLocaleDateString('pt-BR')}
                                        </Td>
                                        <Td fontSize="xs" fontWeight="800" color="textPrimary">
                                            {v.marca}
                                        </Td>
                                        <Td fontSize="xs" color="textSecondary">{v.solucao}</Td>
                                        <Td>
                                            <PrioridadeBadge prioridade={v.prioridade} />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                </Panel>
            </Grid>

            <Panel title={`Marcas Integradas (${marcas.length})`}>
                <Box overflowX="auto" maxH="400px" overflowY="auto">
                    <Table size="sm" variant="unstyled">
                        <Thead position="sticky" top={0} bg="surface" zIndex={1}>
                            <Tr>
                                <Th color="secondary" fontSize="0.65rem">Marca</Th>
                                <Th color="secondary" fontSize="0.65rem">Provedor</Th>
                                <Th color="secondary" fontSize="0.65rem">App</Th>
                                <Th color="secondary" fontSize="0.65rem">Site</Th>
                                <Th color="secondary" fontSize="0.65rem">Painel</Th>
                                <Th color="secondary" fontSize="0.65rem">Venda Direta</Th>
                                <Th color="secondary" fontSize="0.65rem">Responsável</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {marcas.map((m) => (
                                <Tr key={m.id} _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.50' }}>
                                    <Td fontWeight="800" color="textPrimary" fontSize="xs">{m.nome}</Td>
                                    <Td fontSize="0.7rem" color="textSecondary">{m.provedor || '—'}</Td>
                                    <Td>
                                        <Badge
                                            fontSize="0.6rem"
                                            bg={toBool(m.status_possui_aplicativo) ? 'rgba(2,234,117,0.15)' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100')}
                                            color={toBool(m.status_possui_aplicativo) ? 'secondary' : 'textSecondary'}
                                        >
                                            {toBool(m.status_possui_aplicativo) ? 'SIM' : 'NÃO'}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <Badge
                                            fontSize="0.6rem"
                                            bg={toBool(m.site_feito_contel) ? 'rgba(2,234,117,0.15)' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100')}
                                            color={toBool(m.site_feito_contel) ? 'secondary' : 'textSecondary'}
                                        >
                                            {toBool(m.site_feito_contel) ? 'CONTEL' : '—'}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <Badge
                                            fontSize="0.6rem"
                                            bg={toBool(m.painel_feito_contel) ? 'rgba(2,234,117,0.15)' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100')}
                                            color={toBool(m.painel_feito_contel) ? 'secondary' : 'textSecondary'}
                                        >
                                            {toBool(m.painel_feito_contel) ? 'CONTEL' : '—'}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <Badge
                                            fontSize="0.6rem"
                                            bg={toBool(m.venda_direta_feito_contel) ? 'rgba(2,234,117,0.15)' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100')}
                                            color={toBool(m.venda_direta_feito_contel) ? 'secondary' : 'textSecondary'}
                                        >
                                            {toBool(m.venda_direta_feito_contel) ? 'CONTEL' : '—'}
                                        </Badge>
                                    </Td>
                                    <Td fontSize="0.65rem" color="textSecondary">
                                        {m.responsavel_marca_nome || '—'}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            </Panel>
        </VStack>
    );
};

export default Dashboard;
